import { useTranslation } from 'react-i18next';
import { useWatch } from 'react-hook-form';
import { isDeepEqual } from 'utils/objectUtils';
import { differenceWith } from 'utils/arrayUtils';
import { BaseOption } from './MultiSelectField.types';
import {
  SELECT_ALL_OPTION_VALUE,
  isNotSelectAll,
  isSelectAll,
} from './MultiSelectField.constants';

interface UseHandleSelectAllOptionArgs {
  name: string;
  options: BaseOption[];
  fixedOptions?: BaseOption[];
}

function useHandleSelectAllOption({
  name,
  options,
  fixedOptions,
}: UseHandleSelectAllOptionArgs) {
  const { t } = useTranslation();
  const currentSelected: BaseOption[] | undefined = useWatch({ name }) ?? [];

  const selectAllOption = {
    label: t('form.multiSelectField.selectAll'),
    value: SELECT_ALL_OPTION_VALUE,
  };

  const activeOptions = options?.filter((opt) => !opt?.disabled);

  const handleOnChangeWithSelectAll = (
    newValue: BaseOption[],
    _actionMeta: unknown,
    onChange: (value: BaseOption[]) => void,
  ) => {
    // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    const addedPills = differenceWith(newValue, currentSelected, isDeepEqual);
    // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    const removedPills = differenceWith(currentSelected, newValue, isDeepEqual);
    const isAddingSelectAll = !!addedPills.find(isSelectAll);
    const isRemovingSelectAll = !!removedPills.find(isSelectAll);
    // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    const currentlyHasSelectAll = !!currentSelected.find(isSelectAll);
    const isIndividuallySelectingEachOption =
      activeOptions.length && isDeepEqual(activeOptions, newValue);

    if (isRemovingSelectAll) {
      onChange(fixedOptions?.length ? fixedOptions : []);
    } else if (isAddingSelectAll || isIndividuallySelectingEachOption) {
      onChange([selectAllOption, ...activeOptions]);
    } else if (currentlyHasSelectAll && removedPills.length > 0) {
      onChange(newValue.filter(isNotSelectAll));
    } else {
      onChange(newValue);
    }
  };

  return { selectAllOption, handleOnChangeWithSelectAll };
}

export default useHandleSelectAllOption;
